/**
 * search-http — live HTTP implementation of :class:`SearchPort` (PR-4a, M2).
 *
 * Wraps the two BE search surfaces and projects both wires onto one
 * ``SourceResult`` view-model:
 *   - ``POST /api/v1/search/fragments``    → articles (``results[]``)
 *   - ``POST /api/v1/jurisprudence/search`` → case law (``items[]``)
 *
 * Both endpoints serialise **snake_case** (no by-alias config, unlike
 * citation-registry/resolve which is camelCase) — so the wire readers below
 * use ``ref_key`` / ``country_code`` / ``composite_score``, NOT camelCase.
 *
 * Hand-rolled validation (no zod), per the ``auth-http`` / resolver
 * precedent. The port needs a fixed projection, so duck-typing covers the
 * contract and the drift surface stays small.
 *
 * W-invariants honoured:
 *   - W1: the wire→entity mappers are pure field projection (rename + a 1:1
 *     ``contradicts → against`` vocabulary translation). No legal decision
 *     is computed here — ``qual`` is BE-computed, ``ref_key`` is BE-built.
 *   - W2: this is the only place that knows the wire shape; ``domain/`` and
 *     ``ui/`` never see it.
 *   - Country-code clean-cut: reads ``country_code`` only, never ``family``
 *     (the mid-flight ``Family`` umbrella). Lands on the post-migration side.
 */

import type { SearchPort, SourceFilters } from '@/domain/sources/ports'
import type { SourceResult, SourceQual } from '@/domain/sources/entity'
import type { HttpErrorKind } from './_errors'

export interface SearchHttpConfig {
  /** Backend base URL (no trailing slash). */
  baseUrl: string
  /** Lazy bearer accessor — read at call time (login/logout mid-session). */
  getAccessToken: () => string | null
  /** Fetch override for tests. Defaults to the global. */
  fetch?: typeof fetch
}

/** Discriminated error against the shared ``HttpErrorKind`` vocabulary. */
class SearchHttpError extends Error {
  constructor(
    public readonly kind: HttpErrorKind,
    message: string,
  ) {
    super(message)
    this.name = 'SearchHttpError'
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function readString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function readStringOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

function readNumber(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function joinMeta(parts: ReadonlyArray<string>): string {
  return parts.filter((p) => p.length > 0).join(' · ')
}

/** ``contradicts`` (BE) → ``against`` (add-in UI). Single-site translation
 *  — a vocabulary change on either side breaks here loudly. */
function translateQual(raw: unknown): SourceQual {
  switch (raw) {
    case 'nuances':
      return 'nuances'
    case 'contradicts':
      return 'against'
    case 'supports':
    default:
      return 'supports'
  }
}

function mapArticleHit(raw: unknown): SourceResult {
  const w = isPlainObject(raw) ? raw : {}
  const breadcrumb = Array.isArray(w.breadcrumb)
    ? (w.breadcrumb.filter((b) => typeof b === 'string') as string[])
    : []
  return {
    id: readString(w.id),
    refKey: readStringOrNull(w.ref_key),
    kind: 'article',
    name: readString(w.label),
    meta: joinMeta([readString(w.corpus_name), ...breadcrumb]),
    snippet: readString(w.snippet),
    countryCode: readString(w.country_code),
    score: readNumber(w.score),
    ecli: null,
    qual: undefined,
  }
}

/** Pick the highest-scoring matched fact's ``raw`` text for the snippet.
 *  The JP wire carries no flat snippet field — the display text comes from
 *  the matched facts list. */
function pickJpSnippet(facts: unknown): string {
  if (!Array.isArray(facts) || facts.length === 0) return ''
  let best: Record<string, unknown> | null = null
  let bestScore = -Infinity
  for (const f of facts) {
    if (!isPlainObject(f)) continue
    const s = readNumber(f.score)
    if (s >= bestScore) {
      bestScore = s
      best = f
    }
  }
  return best ? readString(best.raw) : ''
}

function mapJpHit(raw: unknown): SourceResult {
  const w = isPlainObject(raw) ? raw : {}
  const caseNumber = readString(w.case_number)
  const ecli = readStringOrNull(w.ecli)
  return {
    id: readString(w.document_id),
    refKey: readStringOrNull(w.ref_key),
    kind: 'jp',
    // Mechanical display name — the canonical legal citation is rendered at
    // insert time (PR-4b) via renderCitation; here we only label the card.
    name: caseNumber || ecli || '',
    meta: joinMeta([readString(w.court), readString(w.chamber)]),
    snippet: pickJpSnippet(w.facts),
    countryCode: readString(w.country_code),
    // composite_score = the combined relevance the BE ranks on.
    score: readNumber(w.composite_score),
    ecli,
    qual: translateQual(w.qualification),
  }
}

export function createSearchHttp(config: SearchHttpConfig): SearchPort {
  const doFetch =
    config.fetch ?? ((input, init) => globalThis.fetch(input, init))

  async function post(path: string, body: unknown): Promise<unknown> {
    const token = config.getAccessToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) headers['Authorization'] = `Bearer ${token}`

    let res: Response
    try {
      res = await doFetch(`${config.baseUrl}${path}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
      })
    } catch (err) {
      throw new SearchHttpError(
        'network',
        err instanceof Error ? err.message : 'Réseau injoignable',
      )
    }

    if (res.status === 401) {
      throw new SearchHttpError('auth', 'Session expirée — reconnectez-vous')
    }
    if (res.status === 429) {
      throw new SearchHttpError(
        'rate_limited',
        'Trop de recherches — patientez une minute',
      )
    }
    if (!res.ok) {
      throw new SearchHttpError('server', `Recherche a échoué (HTTP ${res.status})`)
    }

    let raw: unknown
    try {
      raw = await res.json()
    } catch (err) {
      throw new SearchHttpError(
        'malformed',
        err instanceof Error ? `Réponse JSON invalide: ${err.message}` : 'Réponse JSON invalide',
      )
    }
    if (!isPlainObject(raw)) {
      throw new SearchHttpError('malformed', 'Réponse de recherche non-objet')
    }
    return raw
  }

  return {
    async searchArticles(
      query: string,
      filters?: SourceFilters,
    ): Promise<SourceResult[]> {
      const body: Record<string, unknown> = { query }
      if (filters?.countryCodes && filters.countryCodes.length > 0) {
        body.country_codes = filters.countryCodes
      }
      const raw = (await post('/api/v1/search/fragments', body)) as {
        results?: unknown
      }
      const results = Array.isArray(raw.results) ? raw.results : []
      return results.map(mapArticleHit)
    },

    async searchCaseLaw(
      query: string,
      filters?: SourceFilters,
    ): Promise<SourceResult[]> {
      const body: Record<string, unknown> = { query }
      // Jurisprudence request takes a SINGULAR country_code (not a list);
      // narrow the optional list to its first entry.
      const cc = filters?.countryCodes?.[0]
      if (cc) body.country_code = cc
      const raw = (await post('/api/v1/jurisprudence/search', body)) as {
        items?: unknown
      }
      const items = Array.isArray(raw.items) ? raw.items : []
      return items.map(mapJpHit)
    },
  }
}

export { SearchHttpError }
